import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, RefreshCw, User as UserIcon, Calendar } from "lucide-react";

/**
 * 📥 Confirmări multi-șantier (admin)
 *
 * Lista persoanelor care au ore raportate de pe 2+ șantiere în aceeași zi.
 * Adminul decide: confirmă ambele (a lucrat realmente pe ambele) sau păstrează doar
 * o alocație (eliminând restul + scăzând orele).
 */
export default function ConfirmariMultiSantier() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/multi-site-pending");
      setItems(data.items || []);
    } catch (e) {
      console.error(e);
      toast.error("Eroare la încărcare");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin) return <div className="p-6 text-sm text-zinc-500">Acces restricționat. Doar admin.</div>;

  const confirmAll = async (it) => {
    setBusy(it.entry_id);
    try {
      await api.post(`/admin/multi-site-pending/${it.entry_id}/resolve`, { all: true });
      toast.success("Confirmat — a lucrat pe ambele șantiere");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); } finally { setBusy(null); }
  };

  const keepOnly = async (it, alloc) => {
    setBusy(it.entry_id);
    try {
      await api.post(`/admin/multi-site-pending/${it.entry_id}/resolve`, {
        all: false,
        keep_keys: [{ project_id: alloc.project_id || null, regie_id: alloc.regie_id || null }],
      });
      toast.success(`Păstrat doar ${alloc.label || "alocația selectată"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); } finally { setBusy(null); }
  };

  return (
    <div className="p-2" data-testid="page-confirmari-multi-santier">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
            <AlertTriangle className="text-red-600" size={28}/> Confirmări multi-șantier
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Persoane cu ore raportate de pe 2+ șantiere în aceeași zi. Confirmă dacă au lucrat pe ambele sau păstrează doar una.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="confirm-refresh-btn">
          <RefreshCw size={14} className={loading ? "animate-spin mr-1" : "mr-1"}/> Reîncarcă
        </Button>
      </div>

      {loading ? (
        <div className="card-flat p-8 text-center text-sm text-zinc-500 italic">Se încarcă…</div>
      ) : items.length === 0 ? (
        <div className="card-flat p-8 text-center" data-testid="confirm-empty">
          <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-2"/>
          <div className="text-sm font-semibold text-zinc-700">Nimic de confirmat — niciun conflict multi-șantier!</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.entry_id} className="card-flat overflow-hidden border-2 border-red-300" data-testid={`confirm-item-${it.entry_id}`}>
              <div className="bg-red-50 px-4 py-3 border-b border-red-200 flex flex-wrap items-center gap-3">
                <UserIcon size={18} className="text-red-700"/>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-red-900">{it.person_name}</div>
                  <div className="text-xs text-red-700 flex items-center gap-2 mt-0.5">
                    <Calendar size={12}/> {it.date} · total <strong>{it.total_hours}h</strong> + <strong>{it.total_hours_driven}h</strong> conduse
                  </div>
                </div>
                <Button size="sm" onClick={() => confirmAll(it)} disabled={busy === it.entry_id} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`confirm-all-${it.entry_id}`}>
                  ✓ Da, a lucrat pe ambele
                </Button>
              </div>
              <div className="divide-y divide-zinc-100">
                {it.allocations.map((a, i) => {
                  const allocLoggedAt = a.logged_at || it.logged_at;
                  const allocLoggedBy = a.logged_by_name || it.logged_by_name;
                  return (
                  <div key={i} className="px-4 py-2.5 flex items-center gap-3 hover:bg-zinc-50">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{a.label || (a.project_id ? "Proiect" : "Regie")} {a.regie_id && <span className="text-[9px] px-1 rounded bg-amber-100 text-amber-700 font-bold uppercase ml-1">regie</span>}</div>
                      <div className="text-xs text-zinc-600 mt-0.5">
                        <strong>{a.hours || 0}h</strong> lucrate + <strong>{a.hours_driven || 0}h</strong> conduse
                      </div>
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[11px]" data-testid={`audit-${it.entry_id}-${i}`}>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold border ${allocLoggedBy ? "bg-blue-50 border-blue-200 text-blue-800" : "bg-zinc-100 border-zinc-200 text-zinc-500"}`}>
                          📥 Introdus de: <span className="font-bold">{allocLoggedBy || "necunoscut"}</span>
                        </span>
                        {allocLoggedAt && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold bg-zinc-50 border border-zinc-200 text-zinc-700">
                            🕒 {new Date(allocLoggedAt).toLocaleString("ro-RO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => keepOnly(it, a)} disabled={busy === it.entry_id} data-testid={`confirm-keep-${it.entry_id}-${i}`}>
                      Păstrează doar asta
                    </Button>
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
